import * as React from 'react';
import { createPortal } from 'react-dom';
import axios from '../../utils/axios';
import { cn } from '../../lib/utils';
import Modal, { BotaoPrimario, BotaoSecundario } from './Modal';
import CaixaBusca from './CaixaBusca';
import { Num } from './index';

/* ============================================================================
   ModalSelecionar - escolher UM ou VARIOS itens numa lista PAGINADA do servidor.

   POR QUE EXISTE (21/10/2026, a pedido: "do jeito que esta esta consumindo").
   Os seletores dos modais carregavam a lista ao ABRIR o formulario - grupos com
   rows=9999 em /entidades/paginadoPrincipal, APKs com rows=9999, aplicativos com
   rows=5000 - e ainda consultavam de novo a cada pausa na digitacao. Abrir o
   cadastro de aplicativo para mudar o nome baixava os 4.952 grupos.

   Agora o formulario nao carrega nada: o campo tem uma lupa, e so quem clica
   nela (ou aperta Enter no campo) abre este modal, que pede 20 por pagina ao
   MESMO endpoint paginado que a tela ja usava, com o mesmo filtro (status,
   regional, somenteComPoliticas...). A busca daqui tambem e por Enter/lupa.

   As marcacoes ficam num rascunho local e so valem no "Confirmar": fechar pelo
   X ou Escape descarta, e o formulario de fora continua como estava.

   `maximo` respeita os tetos que ja existem (10 grupos por vez em
   ModalAplicativo/ModalLimparCache): ao chegar nele, os desmarcados travam e a
   tela diz por que.
   ============================================================================ */

export type OpcaoAuto = {
  codigo: string | number;
  rotulo: string;
  /** Linha de apoio (codigo, pacote, regional) mostrada abaixo do rotulo. */
  secundario?: string;
  /** Registro original do servidor. Varios modais gravam o OBJETO inteiro
      (ex.: `entidade` em /dispositivos/mudarGrupo), nao so o codigo. */
  dado?: any;
};

export type BuscarOpcoes = (termo: string, pagina: number, linhas: number) => Promise<{ itens: OpcaoAuto[]; total: number }>;

/** Monta um `BuscarOpcoes` para os endpoints `/paginado` do sistema, que
    respondem `{ content, totalElements }`. */
export function buscarPaginado(
  montarUrl: (termo: string, pagina: number, linhas: number) => string,
  mapear: (item: any) => OpcaoAuto
): BuscarOpcoes {
  return async (termo, pagina, linhas) => {
    const r = await axios.get<{ content: any[]; totalElements?: number | string }>(montarUrl(termo, pagina, linhas));
    const content = r.data?.content ?? [];
    return { itens: content.map(mapear), total: Number(r.data?.totalElements) || content.length };
  };
}

const LINHAS = 20;

const mesmo = (a: OpcaoAuto, b: OpcaoAuto) => String(a.codigo) === String(b.codigo);

/** Monte SO quando for abrir (`{aberto && <ModalSelecionar .../>}`): o estado
    nasce de `inicial`/`termoInicial`, e desmontar ao fechar descarta o rascunho. */
export default function ModalSelecionar({
  titulo,
  buscar,
  multiplo,
  inicial,
  termoInicial = '',
  maximo,
  placeholder = 'Pesquisar',
  vazio = 'Nada encontrado.',
  aoConfirmar,
  onFechar,
}: {
  titulo: string;
  buscar: BuscarOpcoes;
  multiplo?: boolean;
  inicial: OpcaoAuto[];
  termoInicial?: string;
  maximo?: number;
  placeholder?: string;
  vazio?: string;
  aoConfirmar: (lista: OpcaoAuto[]) => void;
  onFechar: () => void;
}) {
  const [termo, setTermo] = React.useState(termoInicial.trim());
  const [pagina, setPagina] = React.useState(0);
  const [itens, setItens] = React.useState<OpcaoAuto[]>([]);
  const [total, setTotal] = React.useState(0);
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState(false);
  const [marcados, setMarcados] = React.useState<OpcaoAuto[]>(inicial);
  const pedido = React.useRef(0);

  // `buscar` costuma vir como arrow inline do chamador; por ref para nao
  // refazer a consulta a cada render dele.
  const refBuscar = React.useRef(buscar);
  refBuscar.current = buscar;

  React.useEffect(() => {
    const meu = ++pedido.current;
    setCarregando(true);
    setErro(false);
    refBuscar
      .current(termo.trim(), pagina, LINHAS)
      .then((r) => {
        if (meu !== pedido.current) return; // resposta de busca antiga
        setItens(r.itens);
        setTotal(r.total);
      })
      .catch(() => {
        if (meu !== pedido.current) return;
        setItens([]);
        setTotal(0);
        setErro(true);
      })
      .finally(() => meu === pedido.current && setCarregando(false));
  }, [termo, pagina]);

  /* Escape fecha SO este modal. Os dois (este e o formulario de fora) escutam
     `keydown` na window; na fase de captura este chega antes e segura o evento,
     senao um Escape fecharia o formulario inteiro junto - perda de trabalho. */
  React.useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onFechar();
    };
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, [onFechar]);

  const estaMarcado = (o: OpcaoAuto) => marcados.some((m) => mesmo(m, o));
  const noTeto = Boolean(multiplo && maximo && marcados.length >= maximo);

  function alternar(o: OpcaoAuto) {
    if (!multiplo) {
      setMarcados(estaMarcado(o) ? [] : [o]);
      return;
    }
    if (estaMarcado(o)) setMarcados((m) => m.filter((x) => !mesmo(x, o)));
    else if (!noTeto) setMarcados((m) => [...m, o]);
  }

  const paginas = Math.max(1, Math.ceil(total / LINHAS));

  /* Portal para o body: este modal abre de DENTRO de outro, e no fluxo normal
     ele herdaria o <label> do `Campo` e a rolagem do card de fora. */
  return createPortal(
    <Modal
      aberto
      titulo={titulo}
      descricao={multiplo ? 'Marque um ou mais itens e confirme.' : 'Marque um item e confirme.'}
      icone="🔍"
      largura="max-w-[560px]"
      onFechar={onFechar}
      rodape={
        <>
          <BotaoSecundario onClick={onFechar}>Cancelar</BotaoSecundario>
          <BotaoPrimario
            onClick={() => {
              aoConfirmar(marcados);
              onFechar();
            }}
          >
            Confirmar{multiplo ? ` (${marcados.length})` : ''}
          </BotaoPrimario>
        </>
      }
    >
      <CaixaBusca
        valor={termo}
        aoBuscar={(v) => {
          setPagina(0);
          setTermo(v);
        }}
        placeholder={placeholder}
        autoFocus
      />

      {noTeto && (
        <div className="rounded-lg border border-warn bg-warn-soft px-3 py-2 text-[11.5px] text-warn">
          No máximo {maximo} por vez. Desmarque um para escolher outro.
        </div>
      )}

      <div role="listbox" aria-multiselectable={multiplo} className="relative min-h-[120px] rounded-lg border border-line">
        {itens.map((o) => {
          const marcado = estaMarcado(o);
          const travado = !marcado && noTeto;
          return (
            <button
              key={o.codigo}
              type="button"
              role="option"
              aria-selected={marcado}
              disabled={travado}
              onClick={() => alternar(o)}
              className={cn(
                'flex w-full items-center gap-2 border-b border-line px-2.5 py-1.5 text-left last:border-b-0 hover:bg-surface-2 disabled:opacity-40',
                marcado && 'bg-primary/10'
              )}
            >
              <span
                className={cn(
                  'grid h-3.5 w-3.5 flex-none place-items-center border-[1.5px] text-[10px] text-primary-foreground',
                  multiplo ? 'rounded' : 'rounded-full',
                  marcado ? 'border-primary bg-primary' : 'border-line'
                )}
              >
                {marcado ? '✓' : ''}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-semibold">{o.rotulo}</span>
                {o.secundario && <span className="block truncate text-[11px] text-faint">{o.secundario}</span>}
              </span>
            </button>
          );
        })}

        {!carregando && !itens.length && (
          <div className="px-3 py-6 text-center text-[12px] text-faint">
            {erro ? 'Não foi possível carregar a lista.' : vazio}
          </div>
        )}
        {carregando && (
          <div className="absolute inset-0 grid place-items-center bg-surface/60 text-[12px] text-muted-foreground">
            Carregando...
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-[11.5px] text-muted-foreground">
        <span>
          <Num>{total.toLocaleString('pt-BR')}</Num> encontrado(s)
          {multiplo && (
            <>
              {' '}
              · <Num>{marcados.length}</Num> marcado(s)
            </>
          )}
        </span>
        <span className="flex items-center gap-2">
          <BotaoSecundario className="h-7 px-2" disabled={pagina === 0 || carregando} onClick={() => setPagina((p) => p - 1)}>
            ‹ Anterior
          </BotaoSecundario>
          <span>
            <Num>{pagina + 1}</Num> de <Num>{paginas}</Num>
          </span>
          <BotaoSecundario
            className="h-7 px-2"
            disabled={pagina + 1 >= paginas || carregando}
            onClick={() => setPagina((p) => p + 1)}
          >
            Próxima ›
          </BotaoSecundario>
        </span>
      </div>
    </Modal>,
    document.body
  );
}
